<template>
    <div>
      <h1 class="text-3xl font-bold text-blue-800">Tableau de bord</h1>

      <div class="grid px-5">    
        <div class="col-4 filter-container ">
          <h2 class="text-lg ">FILTRES</h2> 
          
          <p-select class="mb-4"
            label="Mairie" 
            optionLabel="text" 
            placeholder="Sélectionnez une mairie"
            v-model="selectedMairie"
            :options="mairieOptions"
          />

          <p-select
            label="Période"
            optionLabel="text"
            placeholder="Sélectionnez une période"
            :options="filterOptions"
            v-model="selectedFilter"
            optionValue="value"
            :showClear="true"
          />

         <div v-if="selectedFilter === 'custom'" class="grid custom-filter-container p-4">
            <div class="col-auto mr-3 my-1">
              <p-calendar 
                v-model="startDate" 
                label="Date de début" 
                placeholder="Date de début" 
                dateFormat="dd/mm/yy" 
              />
            </div>
            <div class="col-auto mr-3 my-1">
              <p-calendar 
                v-model="endDate" 
                label="Date de fin" 
                placeholder="Date de fin" 
                dateFormat="dd/mm/yy" 
              />
            </div>
            <button @click="applyCustomFilter" class="p-button p-button-sm my-1">Valider</button>
          </div>
        </div>
        <div class="col px-4 indicators-container">
          <h2 class="text-lg">INDICATEURS</h2>
          <div class="">
            <stat-container>
              <template #cards>
                <stat-card
                    icon="person_add" 
                    title="Naissances" 
                    :value= "totalBirths"
                    colorClass="flat-blue-color"
                />
                <stat-card
                    icon="person_remove" 
                    title="Décès" 
                    :value= "totalDeaths"
                    colorClass="flat-blue-color"
                />
                
              </template>
            </stat-container>

          </div>             
        </div>
      </div>

      <div class="grid mt-4">
        <h2 class="text-lg col-12 ml-4">GRAPHIQUES</h2>
        <div class="col-8">
          <div class="" style="background-color: white; border-radius: 8px">
            <h3 class="text-base p-2">Répartition des Naissances et Décès par mois</h3>
            <Chart 
              type="bar"  
              :data="chartData" 
              :options="chartOptions" 
              style="height: 500px;" 
            /> 
          </div>
        </div>
        <div class="col-4">
          <div class=" mb-1" style="background-color: white; border-radius: 8px">
            <h3 class="text-base p-2">Naissances: Répartition par sexe</h3>
            <div class="card flex justify-center">
              <Chart 
                type="pie" 
                :data="genderBirthChartData"
                :options="genderBirthChartOptions"
                class="w-full ml-8 md:w-[30rem]"          
              />
            </div>
          </div>
          <div class="chart container" style="background-color:white; border-radius: 8px">
            <h3 class="text-base p-2">Décès: Répartition par sexe</h3>
            <div class="card  justify-center">
              <Chart 
                type="doughnut" 
                :data="genderDeathChartData"
                :options="genderDeathChartOptions"
                class="w-full ml-8 md:w-[30rem]"              
              />
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted, watch } from 'vue';
  import Card from 'primevue/card';
  import Chart from 'primevue/chart';
  import { useStatisticsStore } from '@/store/statistics';
  import PSelect from '@/components/form/form-select/p-select.vue';
  import StatContainer from '@/components/stat-container/stat-container.vue';
  import StatCard from '@/components/stat-container/stat-card/stat-card.vue';
  import PCalendar from 'primevue/calendar';
  
  export default defineComponent({
    name: 'AccueilPage',
    components: { Chart, PSelect, PCalendar, StatContainer, StatCard },
    setup() {
      const dashBoardStore = useStatisticsStore();
      const selectedMairie = ref<string | null>(null);
      const selectedFilter = ref<string | null>(null);
      const startDate = ref<Date | null>(null); 
      const endDate = ref<Date | null>(null);

      const mairieOptions = ref([]);
  
      const filterOptions = ref([
        { text: 'Mois dernier', value: 'lastMonth' },
        { text: 'Dernier trimestre', value: 'lastQuarter' },
        { text: 'Dernier semestre', value: 'lastSemester' },
        { text: 'Année en cours', value: 'CurrentYear' },
        { text: 'Année en précédente', value: 'PreviousYear' },
        { text: 'Personnalisé', value: 'custom' },
      ]);
  
      const applyCustomFilter = async () => {
      const startDateStr = startDate.value ? startDate.value.toISOString() : null;
      const endDateStr = endDate.value ? endDate.value.toISOString() : null;
        await dashBoardStore.applyDateFilter(startDateStr, endDateStr);
      };
  
      const onDateChanged = async () => {
        if (selectedFilter.value === 'lastMonth') {
          await dashBoardStore.filterLastMonth();
        } else if (selectedFilter.value === 'lastQuarter') {
          await dashBoardStore.filterLastQuarter();
        } else if (selectedFilter.value === 'lastSemester') {
          await dashBoardStore.filterLastSemester();
        } else if (selectedFilter.value === 'CurrentYear') {
          await dashBoardStore.filterCurrentYear();
        }else if (selectedFilter.value === 'PreviousYear') {
          await dashBoardStore.filterPreviousYear();
        } else {
          dashBoardStore.resetData();
        }
      };
  
      watch(selectedFilter, onDateChanged);
  
      const setChartOptions = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--p-text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
        const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');
        
  
        return {
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: {
              labels: {
                color: textColor,
              },
            },
          },
          scales: {
            x: {
              ticks: {
                color: textColorSecondary,
                font: {
                  size: 14,
                },
              },
              grid: {
                display: false,
              },
            },
            y: {
              ticks: {
                color: textColorSecondary,
                stepSize: 25,
              },
              grid: {
                color: surfaceBorder,
              },
            },
          },
        };
      };

      const setGenderBirthChartOptions = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue("--p-text-color");

        return {
          plugins: {
            legend: {
              labels: {
                usePointStyle: true,
                color: textColor,
              },
            },
          },
        };
      };

      const setGenderDeathChartOptions = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue("--p-text-color");

        return {
          plugins: {
            legend: {
              labels: {
                usePointStyle: true,
                color: textColor,
              },
            },
          },
        };
      };
  
      const chartOptions = computed(() => setChartOptions());
      const genderBirthChartOptions = computed(() => setGenderBirthChartOptions());
      const genderDeathChartOptions = computed(() => setGenderDeathChartOptions());
      const totalBirths = computed(() => dashBoardStore.totalBirths);
      const totalDeaths = computed(() => dashBoardStore.totalDeaths);
      const chartData = computed(() => dashBoardStore.chartData);
      const genderBirthChartData = computed(() => dashBoardStore.genderBirthChartData);
      const genderDeathChartData = computed(() => dashBoardStore.genderDeathChartData);
      onMounted(async () => {
        await dashBoardStore.loadAllData();
      });
  
      return {
        selectedMairie,
        selectedFilter,
        startDate,
        endDate,
        mairieOptions,
        filterOptions,
        applyCustomFilter,
        totalBirths,
        totalDeaths,
        chartData,
        chartOptions,
        genderBirthChartOptions,
        genderBirthChartData,
        genderDeathChartOptions,
        genderDeathChartData,
      };
    },
  });
</script>
  
  <style lang="scss" src="./accueil-page.scss" scoped />
  